
import { useScrollReveal } from '../../../hooks/useScrollReveal';

export default function Framework() {
  const { ref, isVisible } = useScrollReveal(0.1);

  const steps = [
    {
      number: '01',
      title: 'Audit & Diagnose',
      description: 'We analyze operational gaps and automation opportunities across your entire workflow.',
      icon: 'ri-search-eye-line',
    },
    {
      number: '02',
      title: 'Architect the System',
      description: 'We design a structured AI infrastructure tailored to your industry and growth goals.',
      icon: 'ri-draft-line',
    },
    {
      number: '03',
      title: 'Automate & Deploy',
      description: 'We integrate workflows, CRM systems, and AI layers into a seamless operation.',
      icon: 'ri-rocket-2-line',
    },
    {
      number: '04',
      title: 'Optimize & Scale',
      description: 'We refine performance metrics and unlock sustainable, compounding growth.',
      icon: 'ri-line-chart-line',
    },
  ];

  return (
    <section id="framework" className="relative py-36 px-6 bg-slate-950 overflow-hidden">
      {/* Background Visual */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/1f755ba2cf23bb4565b399a78c79d295.jpg"
          alt="Framework background"
          className="w-full h-full object-cover object-top opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-950/80 to-slate-950" />
      </div>

      <div ref={ref} className="relative z-10 max-w-7xl mx-auto">
        {/* Section Header */}
        <div className={`text-center mb-20 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <span className="text-teal-400 text-xs font-medium tracking-widest uppercase mb-4 block">Our Process</span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
            The Palemia Structure Framework™
          </h2>
          <p className="text-base text-gray-400 max-w-2xl mx-auto">
            A disciplined system for scalable automation — engineered for precision at every stage.
          </p>
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 relative">
          {/* Connector Line (desktop) */}
          <div className="hidden lg:block absolute top-24 left-[12.5%] right-[12.5%] h-px bg-gradient-to-r from-teal-500/10 via-teal-500/30 to-teal-500/10" />

          {steps.map((step, index) => (
            <div
              key={index}
              className={`relative transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: `${(index + 1) * 150}ms` }}
            >
              <div className="group relative bg-slate-900/50 border border-slate-800/50 rounded-2xl p-8 hover:border-teal-500/20 hover:bg-slate-900/80 transition-all duration-500 cursor-pointer h-full">
                {/* Step Number + Icon */}
                <div className="flex items-center justify-between mb-8">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-teal-500/10 border border-teal-500/15 group-hover:bg-teal-500/15 transition-colors duration-300">
                    <i className={`${step.icon} text-xl text-teal-400`}></i>
                  </div>
                  <span className="text-5xl font-bold text-slate-800 group-hover:text-slate-700 transition-colors">
                    {step.number}
                  </span>
                </div>

                {/* Title */}
                <h3 className="text-xl font-bold text-white mb-3 tracking-tight">
                  {step.title}
                </h3>

                {/* Description */}
                <p className="text-gray-400 text-sm leading-relaxed">
                  {step.description}
                </p>

                {/* Bottom accent */}
                <div className="mt-6 h-0.5 w-10 bg-teal-500/30 group-hover:w-16 group-hover:bg-teal-500 transition-all duration-500" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
